<script setup lang="ts">
/**
 * PMS left side-nav. Sectioned list of nav items, each with a leading
 * icon. Active item gets the blue 60 left rule + selected background
 * per the design bundle's `.cds-side-nav__item.is-active`.
 *
 * Two render modes per the source-of-truth design bundle
 * (`.claude/design-bundle/agenda/pms/project/PMS.html` L152–185):
 *  - expanded (`collapsed=false`): section eyebrows + icon + label
 *  - collapsed (`collapsed=true`): icon-only rail, label exposed via
 *    `title` attribute for native hover-tooltip; section headers hidden
 *
 * Pure presentation. The router is wired by the parent.
 */
import { computed } from 'vue'
import CdsIcon, { type CdsIconName } from '../atoms/CdsIcon.vue'

export interface PmsSideNavItem {
  id: string
  label: string
  icon: CdsIconName
}

export interface PmsSideNavSection {
  /** ALL CAPS short eyebrow label. */
  label: string
  items: PmsSideNavItem[]
}

const props = withDefaults(
  defineProps<{
    sections: PmsSideNavSection[]
    activeId?: string
    /** When true, render the icon-only rail (48px column). */
    collapsed?: boolean
  }>(),
  { activeId: undefined, collapsed: false },
)

const emit = defineEmits<{ select: [id: string] }>()

/** Flat list of items used by the collapsed branch (no section eyebrows). */
const flatItems = computed<PmsSideNavItem[]>(() => props.sections.flatMap((s) => s.items))

function pick(item: PmsSideNavItem) {
  emit('select', item.id)
}
</script>

<template>
  <nav v-if="collapsed" class="fz-pms-side-nav fz-pms-side-nav--collapsed" aria-label="Sezioni">
    <button
      v-for="item in flatItems"
      :key="item.id"
      type="button"
      :class="['fz-pms-side-nav__item', { 'is-active': activeId === item.id }]"
      :title="item.label"
      :aria-label="item.label"
      @click="pick(item)"
    >
      <span class="fz-pms-side-nav__icon">
        <CdsIcon :name="item.icon" :size="16" />
      </span>
    </button>
  </nav>
  <nav v-else class="fz-pms-side-nav" aria-label="Sezioni">
    <template v-for="section in sections" :key="section.label">
      <div class="fz-pms-side-nav__section">{{ section.label }}</div>
      <button
        v-for="item in section.items"
        :key="item.id"
        type="button"
        :class="['fz-pms-side-nav__item', { 'is-active': activeId === item.id }]"
        @click="pick(item)"
      >
        <span class="fz-pms-side-nav__icon">
          <CdsIcon :name="item.icon" :size="16" />
        </span>
        {{ item.label }}
      </button>
    </template>
  </nav>
</template>

<style scoped>
.fz-pms-side-nav {
  background: var(--bg);
  border-right: 1px solid var(--border-subtle-00);
  height: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
}
.fz-pms-side-nav__section {
  padding: var(--spacing-05) var(--spacing-05) var(--spacing-03);
  font-size: var(--fs-12);
  text-transform: uppercase;
  letter-spacing: 0.32px;
  color: var(--text-helper);
}
.fz-pms-side-nav__item {
  display: flex;
  align-items: center;
  gap: var(--spacing-04);
  height: 32px;
  padding: 0 var(--spacing-05);
  color: var(--text-secondary);
  font-size: var(--fs-14);
  text-decoration: none;
  cursor: pointer;
  border-left: 3px solid transparent;
  background: transparent;
  border-top: none;
  border-right: none;
  border-bottom: none;
  text-align: left;
  width: 100%;
  font-family: inherit;
}
.fz-pms-side-nav__item:hover {
  background: var(--bg-hover);
  color: var(--text-primary);
}
.fz-pms-side-nav__item:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: -2px;
}
.fz-pms-side-nav__item.is-active {
  color: var(--text-primary);
  font-weight: var(--fw-semibold);
  background: var(--bg-selected);
  border-left-color: var(--blue-60);
}
.fz-pms-side-nav__icon {
  width: 16px;
  height: 16px;
  flex: 0 0 16px;
  color: currentColor;
  display: inline-flex;
}

/* Collapsed icon-rail mode (48px column).
   Mirrors PMS.html L155–164: no labels, no section eyebrows,
   each button padding:0 + justify-content:center so the 16x16 icon
   centres in the 48px column. Active item keeps the 3px blue-60
   left border for state visibility. */
.fz-pms-side-nav--collapsed .fz-pms-side-nav__item {
  padding: 0;
  justify-content: center;
}
</style>
